import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal,
  Building2,
  Users,
  RefreshCcw,
  Eye,
  Flag,
  PowerOff,
  Edit,
} from "lucide-react";
import { toast } from "sonner";

import { StatCard } from "@/components/admin/StatCard";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import { NightCapBar } from "@/components/admin/NightCapBar";
import {
  getUnits,
  getBuildings,
  type UnitFilters,
} from "@/lib/admin/queries";
import {
  updateUnit,
  deactivateUnit,
  resetNightCounter,
  reassignHost,
} from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { Unit } from "@/types/database";

// ─── Types ────────────────────────────────────────────────────────────────────

type UnitRow = Unit & {
  organization?: { id: string; name: string } | null;
  host?: Array<{
    id: string;
    nights_used: number;
    night_limit: number;
    status: string;
    profile?: { id: string; full_name: string; email: string } | null;
  }> | null;
};

type EditFormValues = {
  unit_number: string;
  night_cap_override: number;
  notes: string;
};

type ResetFormValues = {
  reason: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveUnitStatus(
  unit: UnitRow
): "active" | "approaching_limit" | "over_limit" | "inactive" {
  if (unit.status === "inactive" || unit.status === "suspended") return "inactive";
  const host = Array.isArray(unit.host) ? unit.host[0] : null;
  if (!host) return unit.status === "active" ? "active" : "inactive";
  const pct = host.night_limit > 0 ? (host.nights_used / host.night_limit) * 100 : 0;
  if (host.nights_used > host.night_limit) return "over_limit";
  if (pct >= 70) return "approaching_limit";
  return "active";
}

function statusLabel(s: ReturnType<typeof deriveUnitStatus>): string {
  switch (s) {
    case "active": return "Active";
    case "approaching_limit": return "Approaching Limit";
    case "over_limit": return "Over Limit";
    case "inactive": return "Inactive";
  }
}

function statusBadgeStatus(s: ReturnType<typeof deriveUnitStatus>): string {
  switch (s) {
    case "active": return "active";
    case "approaching_limit": return "pending";
    case "over_limit": return "over_limit";
    case "inactive": return "inactive";
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminUnitsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filters
  const [search, setSearch] = useState("");
  const [buildingId, setBuildingId] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Modals / Sheets
  const [editUnit, setEditUnit] = useState<UnitRow | null>(null);
  const [resetUnit, setResetUnit] = useState<UnitRow | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<UnitRow | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<UnitRow | null>(null);
  const [newHostId, setNewHostId] = useState("");

  // Edit form
  const editForm = useForm<EditFormValues>();
  // Reset reason form
  const resetForm = useForm<ResetFormValues>();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const filters: UnitFilters = {
    ...(search ? { search } : {}),
    ...(buildingId !== "all" ? { buildingId } : {}),
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
  };

  const { data: unitsResult, isLoading: unitsLoading } = useQuery({
    queryKey: ["admin", "units", filters, page, pageSize],
    queryFn: () => getUnits(filters, page, pageSize),
  });

  const { data: buildingsResult } = useQuery({
    queryKey: ["admin", "buildings-list"],
    queryFn: () => getBuildings({}, 1, 100),
  });

  const units = (unitsResult?.data ?? []) as UnitRow[];
  const totalCount = unitsResult?.count ?? 0;
  const buildings = buildingsResult?.data ?? [];

  // ─── Mutations ────────────────────────────────────────────────────────────

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: Partial<Unit> }) =>
      updateUnit(vars.id, vars.data, user!.id),
    onSuccess: () => {
      toast.success("Unit updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "units"] });
      setEditUnit(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const resetMutation = useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      resetNightCounter(vars.id, vars.reason, user!.id),
    onSuccess: () => {
      toast.success("Night counter reset");
      queryClient.invalidateQueries({ queryKey: ["admin", "units"] });
      setResetUnit(null);
      setResetConfirmOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deactivateUnit(id, user!.id),
    onSuccess: () => {
      toast.success("Unit deactivated");
      queryClient.invalidateQueries({ queryKey: ["admin", "units"] });
      setDeactivateTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reassignMutation = useMutation({
    mutationFn: (vars: { unitId: string; hostId: string }) =>
      reassignHost(vars.unitId, vars.hostId, user!.id),
    onSuccess: () => {
      toast.success("Host reassigned successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "units"] });
      setReassignTarget(null);
      setNewHostId("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────

  function openEdit(unit: UnitRow) {
    const host = Array.isArray(unit.host) ? unit.host[0] : null;
    editForm.reset({
      unit_number: unit.unit_number,
      night_cap_override: host?.night_limit ?? 0,
      notes: unit.notes ?? "",
    });
    setEditUnit(unit);
  }

  function handleEditSubmit(values: EditFormValues) {
    if (!editUnit) return;
    updateMutation.mutate({
      id: editUnit.id,
      data: {
        unit_number: values.unit_number,
        notes: values.notes,
      },
    });
  }

  function handleResetConfirm() {
    if (!resetUnit) return;
    const reason = resetForm.getValues("reason");
    resetMutation.mutate({ id: resetUnit.id, reason });
  }

  function handleExportCsv() {
    const rows = units.map((u) => {
      const host = Array.isArray(u.host) ? u.host[0] : null;
      return {
        building: u.organization?.name ?? "",
        unit: u.unit_number,
        host_name: host?.profile?.full_name ?? "",
        host_email: host?.profile?.email ?? "",
        nights_used: host?.nights_used ?? 0,
        night_cap: host?.night_limit ?? 0,
        status: deriveUnitStatus(u),
      };
    });
    const header = Object.keys(rows[0] ?? {}).join(",");
    const csv = [header, ...rows.map((r) => Object.values(r).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "units-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // Stats
  const activeCount = units.filter((u) => deriveUnitStatus(u) === "active").length;
  const approachingCount = units.filter((u) => deriveUnitStatus(u) === "approaching_limit").length;
  const overCount = units.filter((u) => deriveUnitStatus(u) === "over_limit").length;

  // ─── Columns ──────────────────────────────────────────────────────────────

  const columns = [
    {
      key: "building",
      label: "Building",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        return (
          <span className="text-sm font-medium text-gray-900">
            {unit.organization?.name ?? <span className="text-gray-400">—</span>}
          </span>
        );
      },
    },
    {
      key: "unit_number",
      label: "Unit #",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        return <span className="font-mono text-sm whitespace-nowrap">{unit.unit_number}</span>;
      },
    },
    {
      key: "host_name",
      label: "Host Name",
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        const host = Array.isArray(unit.host) ? unit.host[0] : null;
        return (
          <span className="truncate max-w-[160px] block">
            {host?.profile?.full_name ?? <span className="text-gray-400">Unassigned</span>}
          </span>
        );
      },
    },
    {
      key: "host_email",
      label: "Host Email",
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        const host = Array.isArray(unit.host) ? unit.host[0] : null;
        return (
          <span className="text-sm text-gray-500 truncate max-w-[160px] block">
            {host?.profile?.email ?? <span className="text-gray-400">—</span>}
          </span>
        );
      },
    },
    {
      key: "nights_used",
      label: "Nights Used",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        const host = Array.isArray(unit.host) ? unit.host[0] : null;
        return (
          <span className="tabular-nums text-sm whitespace-nowrap">
            {host?.nights_used ?? 0}
          </span>
        );
      },
    },
    {
      key: "night_cap",
      label: "Night Cap",
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        const host = Array.isArray(unit.host) ? unit.host[0] : null;
        return (
          <span className="tabular-nums text-sm whitespace-nowrap">
            {host?.night_limit ?? <span className="text-gray-400">—</span>}
          </span>
        );
      },
    },
    {
      key: "pct_used",
      label: "% Used",
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        const host = Array.isArray(unit.host) ? unit.host[0] : null;
        if (!host) return <span className="text-gray-400 text-sm">—</span>;
        const pct =
          host.night_limit > 0
            ? Math.round((host.nights_used / host.night_limit) * 100)
            : 0;
        return (
          <div className="min-w-[120px]">
            <NightCapBar used={host.nights_used} cap={host.night_limit} showLabel={false} />
            <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">{pct}%</span>
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        const derived = deriveUnitStatus(unit);
        return <StatusBadge status={statusBadgeStatus(derived)} size="sm" />;
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row: Record<string, unknown>) => {
        const unit = row as unknown as UnitRow;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => openEdit(unit)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit unit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => {
                  setResetUnit(unit);
                  resetForm.reset({ reason: "" });
                  setResetConfirmOpen(true);
                }}
              >
                <RefreshCcw className="mr-2 h-4 w-4" />
                Reset nights counter
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() =>
                  navigate(`/admin/units/${unit.id}/bookings`)
                }
              >
                <Eye className="mr-2 h-4 w-4" />
                View bookings
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => { setReassignTarget(unit); setNewHostId(""); }}
              >
                <Users className="mr-2 h-4 w-4" />
                Reassign host
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-amber-600 whitespace-nowrap">
                <Flag className="mr-2 h-4 w-4" />
                Flag for review
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600 whitespace-nowrap"
                onClick={() => setDeactivateTarget(unit)}
              >
                <PowerOff className="mr-2 h-4 w-4" />
                Deactivate
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // ─── Filters UI ───────────────────────────────────────────────────────────

  const filtersNode = (
    <>
      <Select
        value={buildingId}
        onValueChange={(v) => { setBuildingId(v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-[180px] text-sm">
          <Building2 className="mr-2 h-4 w-4 text-gray-400" />
          <SelectValue placeholder="All buildings" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All buildings</SelectItem>
          {buildings.map((b) => (
            <SelectItem key={b.id} value={b.id}>
              {b.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={statusFilter}
        onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-[160px] text-sm">
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
          <SelectItem value="suspended">Suspended</SelectItem>
        </SelectContent>
      </Select>
    </>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Units</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {unitsLoading ? "Loading…" : `${totalCount} unit${totalCount !== 1 ? "s" : ""} total`}
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          title="Total Units"
          value={totalCount}
          icon={<Building2 className="h-5 w-5" />}
          color="blue"
          loading={unitsLoading}
        />
        <StatCard
          title="Active"
          value={activeCount}
          icon={<Users className="h-5 w-5" />}
          color="green"
          loading={unitsLoading}
        />
        <StatCard
          title="Approaching Limit"
          value={approachingCount}
          icon={<Flag className="h-5 w-5" />}
          color="amber"
          loading={unitsLoading}
        />
        <StatCard
          title="Over Limit"
          value={overCount}
          icon={<PowerOff className="h-5 w-5" />}
          color="red"
          loading={unitsLoading}
        />
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={units as unknown as Record<string, unknown>[]}
        loading={unitsLoading}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onSearch={(q) => { setSearch(q); setPage(1); }}
        searchPlaceholder="Search unit # or host name…"
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        bulkActions={[
          {
            label: "Export CSV",
            onClick: handleExportCsv,
          },
        ]}
        onExport={handleExportCsv}
        filters={filtersNode}
        emptyMessage="No units found. Adjust your filters."
      />

      {/* Edit Sheet */}
      <Sheet open={!!editUnit} onOpenChange={(v) => { if (!v) setEditUnit(null); }}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit Unit</SheetTitle>
            <SheetDescription>
              Update unit details and night cap override.
            </SheetDescription>
          </SheetHeader>
          <form
            onSubmit={editForm.handleSubmit(handleEditSubmit)}
            className="space-y-4 mt-6"
          >
            <div className="space-y-2">
              <Label htmlFor="unit_number" className="whitespace-nowrap">Unit Number</Label>
              <Input
                id="unit_number"
                {...editForm.register("unit_number", { required: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="night_cap_override" className="whitespace-nowrap">Night Cap Override</Label>
              <Input
                id="night_cap_override"
                type="number"
                min={0}
                {...editForm.register("night_cap_override", {
                  valueAsNumber: true,
                })}
              />
              <p className="text-xs text-gray-500">
                Overrides the building default for this unit.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes" className="whitespace-nowrap">Notes</Label>
              <Textarea
                id="notes"
                rows={3}
                placeholder="Internal admin notes…"
                {...editForm.register("notes")}
              />
            </div>
            <SheetFooter className="pt-4">
              <Button
                variant="outline"
                type="button"
                className="whitespace-nowrap"
                onClick={() => setEditUnit(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending} className="whitespace-nowrap">
                {updateMutation.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Reset Nights Modal — requireTyping + reason textarea */}
      <Sheet
        open={resetConfirmOpen}
        onOpenChange={(v) => { if (!v) { setResetConfirmOpen(false); setResetUnit(null); } }}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Reset Night Counter</SheetTitle>
            <SheetDescription>
              This will zero out the night counter for{" "}
              <strong>Unit {resetUnit?.unit_number}</strong>. This action is
              logged and cannot be undone.
            </SheetDescription>
          </SheetHeader>
          <form
            onSubmit={resetForm.handleSubmit(() => handleResetConfirm())}
            className="space-y-4 mt-6"
          >
            <div className="space-y-2">
              <Label htmlFor="reset_reason" className="whitespace-nowrap">Reason *</Label>
              <Textarea
                id="reset_reason"
                rows={3}
                placeholder="Describe why you are resetting this counter…"
                {...resetForm.register("reason", { required: true })}
              />
              {resetForm.formState.errors.reason && (
                <p className="text-xs text-red-500">Reason is required</p>
              )}
            </div>
            <SheetFooter className="pt-2">
              <Button
                variant="outline"
                type="button"
                className="whitespace-nowrap"
                onClick={() => { setResetConfirmOpen(false); setResetUnit(null); }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                type="submit"
                disabled={resetMutation.isPending}
                className="whitespace-nowrap"
              >
                {resetMutation.isPending ? "Resetting…" : "Reset Counter"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Deactivate Confirm */}
      <ConfirmModal
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
        title="Deactivate Unit"
        description={`Are you sure you want to deactivate Unit ${deactivateTarget?.unit_number}? The unit will be set to inactive and the host will lose booking privileges.`}
        requireTyping
        loading={deactivateMutation.isPending}
        variant="destructive"
      />

      {/* Reassign Host Sheet */}
      <Sheet open={!!reassignTarget} onOpenChange={(v) => { if (!v) { setReassignTarget(null); setNewHostId(""); } }}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Reassign Host</SheetTitle>
            <SheetDescription>
              Reassign Unit {reassignTarget?.unit_number} to a different host by entering their host ID.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label htmlFor="new-host-id" className="whitespace-nowrap">New Host ID</Label>
              <Input
                id="new-host-id"
                value={newHostId}
                onChange={(e) => setNewHostId(e.target.value)}
                placeholder="Host UUID…"
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Enter the UUID of the host record to assign to this unit.
              </p>
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button
              variant="outline"
              className="whitespace-nowrap"
              onClick={() => { setReassignTarget(null); setNewHostId(""); }}
            >
              Cancel
            </Button>
            <Button
              disabled={!newHostId.trim() || reassignMutation.isPending}
              className="whitespace-nowrap"
              onClick={() =>
                reassignTarget &&
                reassignMutation.mutate({ unitId: reassignTarget.id, hostId: newHostId.trim() })
              }
            >
              {reassignMutation.isPending ? "Reassigning…" : "Reassign Host"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
